/**
 * batch-69 tvt-saldi (FO-522 + FO-513) - TVT in de afboekvolgorde, het
 * expliciete saldo-tekort, saldo verplaatsen, herverdelen (incl. TVT), het
 * overzicht "Saldi in de min" en het rechttrekken met TVT.
 *
 * Gedeelde Zod-schema's (API valideert ermee, de backoffice-formulieren
 * valideren er client-side vooraf mee) en de DTO-vormen. Server-veilig: geen
 * React, geen 'use client'.
 */

import { z } from 'zod'
import { WERKTIJD_SPAREN_LABELS } from './werktijd-sparen'
import { queryBoolean } from './zod-query'

// ── Afboekvolgorde (FO-522 §A1) ──────────────────────────────────────────────

/** Volgorde-modus van de automatische afboeking (spiegel van de Prisma-enum). */
export const AFBOEK_VOLGORDE_MODUS = ['VASTE_LIJST', 'VERVALT_EERST'] as const
export type AfboekVolgordeModusDTO = (typeof AFBOEK_VOLGORDE_MODUS)[number]

export const AFBOEK_VOLGORDE_MODUS_LABELS: Record<AfboekVolgordeModusDTO, string> = {
  VASTE_LIJST: 'Vaste volgorde (de lijst hieronder, van boven naar beneden)',
  VERVALT_EERST: 'Wat het eerst vervalt, gaat eerst',
}

/** De emmers die aan de automatische afboeking kunnen meedoen. */
export const AFBOEK_EMMER_TYPES = ['TVT_WERK', 'TVT_REIS', 'ATV', 'WETTELIJK', 'BOVENWETTELIJK'] as const
export type AfboekEmmerType = (typeof AFBOEK_EMMER_TYPES)[number]

/**
 * Schermlabels van de vijf klassieke emmers (woordenlijst FO-344 voor TVT en ATV).
 * Exports en loonstrook-koppelingen houden hun eigen vaktermen.
 */
export const AFBOEK_EMMER_LABELS: Record<AfboekEmmerType, string> = {
  TVT_WERK: WERKTIJD_SPAREN_LABELS.overuren.kort,
  TVT_REIS: WERKTIJD_SPAREN_LABELS.reistijd.kort,
  ATV: WERKTIJD_SPAREN_LABELS.atv.kort,
  WETTELIJK: 'Wettelijk verlof',
  BOVENWETTELIJK: 'Bovenwettelijk verlof',
}

/**
 * Het afboekbeleid zoals het beleidsscherm het opslaat. De lijst is geordend en
 * moet minstens één emmer bevatten; dubbelen zijn niet toegestaan.
 */
export const AfboekBeleidSchema = z
  .object({
    autoAfboekVolgorde: z.enum(AFBOEK_VOLGORDE_MODUS),
    autoAfboekEmmers: z
      .array(z.enum(AFBOEK_EMMER_TYPES))
      .min(1, 'Kies minstens één emmer voor de automatische afboeking'),
  })
  .superRefine((v, ctx) => {
    if (new Set(v.autoAfboekEmmers).size !== v.autoAfboekEmmers.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['autoAfboekEmmers'],
        message: 'Elke emmer mag maar één keer in de volgorde staan',
      })
    }
  })
export type AfboekBeleidInput = z.infer<typeof AfboekBeleidSchema>

// ── TVT-daling na opbouw (FO-513 §C8) ────────────────────────────────────────

/** Wat er gebeurt als de overuren van een week dalen nadat er al TVT is opgebouwd. */
export const TVT_DALING_MODUS = ['ALLEEN_SIGNALEREN', 'CLAWBACK', 'BLOKKEREN'] as const
export type TvtDalingModusDTO = (typeof TVT_DALING_MODUS)[number]

/**
 * Integrator-review B1 - de standaard: niet bijstellen, alleen een signaal aan HR
 * (het gedrag van vóór batch-69 plus een melding). De clawback is een PO-besluit
 * en gaat nooit vanzelf aan. Eén bron voor de Zod-default, de terugval zonder
 * beleid en het beleidsscherm; de drift-test `tvt-daling-standaard-drift` houdt
 * de Prisma-default en de laatste migratie gelijk.
 */
export const STANDAARD_TVT_DALING_MODUS: TvtDalingModusDTO = 'ALLEEN_SIGNALEREN'

export const TVT_DALING_MODUS_LABELS: Record<TvtDalingModusDTO, string> = {
  CLAWBACK: 'Opbouw bijstellen (zichtbare correctie op de gespaarde uren)',
  ALLEEN_SIGNALEREN: 'Niet bijstellen, alleen een signaal aan HR',
  BLOKKEREN: 'Een verlaging van de overuren na de opbouw weigeren',
}

/** Preview "zo wordt een aanvraag van 8 u nu verdeeld" op het beleidsscherm. */
export const AfboekPreviewSchema = z.object({
  autoAfboekVolgorde: z.enum(AFBOEK_VOLGORDE_MODUS),
  autoAfboekEmmers: z.array(z.enum(AFBOEK_EMMER_TYPES)).min(1),
  uren: z
    .number()
    .positive('Vul een positief aantal uren in')
    .max(400, 'Maximaal 400 uur')
    .optional(),
  /** Optioneel: de preview met het echte saldo van deze medewerker. */
  employeeId: z.string().min(1).optional(),
})
export type AfboekPreviewInput = z.infer<typeof AfboekPreviewSchema>

export interface AfboekPreviewDeelDTO {
  emmerType: AfboekEmmerType
  jaar: number
  uren: number
  /** Beschikbaar saldo van die emmer vóór deze aanvraag. */
  beschikbaarVoor: number
}

export interface AfboekPreviewDTO {
  uren: number
  delen: AfboekPreviewDeelDTO[]
  tekort: number
  /** 'MEDEWERKER' = echt saldo van de gekozen medewerker; 'VOORBEELD' = vast rekenvoorbeeld. */
  bron: 'MEDEWERKER' | 'VOORBEELD'
  /** Het (voorbeeld)saldo waarmee is gerekend, per emmer in afboekvolgorde. */
  saldi: Array<{ emmerType: AfboekEmmerType; jaar: number; beschikbaar: number }>
}

// ── Saldo-tekort bij classificatie (FO-522 §A2) ──────────────────────────────

/**
 * De HR-keuze bij een aanvraag die niet volledig door saldo wordt gedekt:
 *   - `NEGATIEF_OP_EMMER`: bewust negatief boeken op een gekozen emmer;
 *   - `ONBETAALD`: het niet-gedekte deel wordt onbetaald verlof (geen saldo).
 * Afwijzen loopt via de bestaande afwijs-route. Reden is altijd verplicht.
 */
export const SALDO_TEKORT_KEUZES = ['NEGATIEF_OP_EMMER', 'ONBETAALD'] as const
export type SaldoTekortKeuze = (typeof SALDO_TEKORT_KEUZES)[number]

export const SALDO_TEKORT_KEUZE_LABELS: Record<SaldoTekortKeuze, string> = {
  NEGATIEF_OP_EMMER: 'Toestaan: negatief boeken op een gekozen emmer',
  ONBETAALD: 'Onbetaald verlof voor het tekort',
}

export const SaldoTekortAfhandelingSchema = z
  .object({
    keuze: z.enum(SALDO_TEKORT_KEUZES),
    emmerType: z.enum(AFBOEK_EMMER_TYPES).optional(),
    reden: z
      .string()
      .trim()
      .min(10, 'Geef een reden van minstens 10 tekens')
      .max(1000, 'Maximaal 1000 tekens'),
  })
  .superRefine((v, ctx) => {
    if (v.keuze === 'NEGATIEF_OP_EMMER' && !v.emmerType) {
      ctx.addIssue({
        code: 'custom',
        path: ['emmerType'],
        message: 'Kies de emmer die negatief mag worden',
      })
    }
  })
export type SaldoTekortAfhandeling = z.infer<typeof SaldoTekortAfhandelingSchema>

/** Foutcode van classify-hr wanneer een tekort een HR-keuze vraagt. */
export const SALDO_TEKORT_CODE = 'SALDO_TEKORT' as const

/** `details` bij een 409 SALDO_TEKORT. */
export interface SaldoTekortDetailsDTO {
  tekortUren: number
  teBoekenUren: number
  /** Wat het saldo wél dekt, in afboekvolgorde. */
  gedekt: Array<{ emmerType: AfboekEmmerType; uren: number }>
  /** Beschikbaar saldo per emmer-type (voor de emmer-keuze). */
  beschikbaarPerType: Array<{ emmerType: AfboekEmmerType; beschikbaar: number }>
}

// ── Saldo verplaatsen (FO-522 §C8, FO-513 §A1) ───────────────────────────────

export const SALDO_VERPLAATS_REDENEN = [
  'RECHTTREKKEN_NEGATIEF',
  'VERLOF_TEN_LASTE_VAN_TVT',
  'OVERIG',
] as const
export type SaldoVerplaatsReden = (typeof SALDO_VERPLAATS_REDENEN)[number]

export const SALDO_VERPLAATS_REDEN_LABELS: Record<SaldoVerplaatsReden, string> = {
  RECHTTREKKEN_NEGATIEF: 'Rechttrekken negatief saldo',
  VERLOF_TEN_LASTE_VAN_TVT: 'Verlof ten laste van TVT',
  OVERIG: 'Overig',
}

const urenMax2Decimalen = z
  .number({ message: 'Vul een aantal uren in' })
  .positive('Het aantal uren moet groter dan 0 zijn')
  .max(2000, 'Maximaal 2000 uur')
  .refine((v) => Math.abs(v * 100 - Math.round(v * 100)) < 1e-9, {
    message: 'Uren mogen maximaal 2 decimalen hebben (bv. 7,6)',
  })

/**
 * `POST /leave/v2/saldo/:employeeId/verplaats`. `dryRun` = alleen de preview
 * (saldi vóór en na); zonder `dryRun` één transactie met twee gekoppelde
 * mutaties. Het jaar van beide emmers is standaard het jaar van de peildatum.
 */
export const SaldoVerplaatsSchema = z
  .object({
    van: z.enum(AFBOEK_EMMER_TYPES),
    naar: z.enum(AFBOEK_EMMER_TYPES),
    vanJaar: z.number().int().min(2020).max(2100).optional(),
    naarJaar: z.number().int().min(2020).max(2100).optional(),
    uren: urenMax2Decimalen,
    redenCode: z.enum(SALDO_VERPLAATS_REDENEN),
    toelichting: z.string().trim().max(1000, 'Maximaal 1000 tekens').optional(),
    /** yyyy-mm-dd (Europe/Amsterdam-kalenderdag); standaard vandaag. */
    peildatum: z.string().date('Kies een geldige peildatum').optional(),
    /** Verplicht als de bron-emmer door de verplaatsing negatief wordt. */
    bevestigNegatief: z.boolean().optional(),
    dryRun: z.boolean().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.van === v.naar && (v.vanJaar ?? 0) === (v.naarJaar ?? 0)) {
      ctx.addIssue({ code: 'custom', path: ['naar'], message: 'Kies een andere doel-emmer dan de bron' })
    }
    if (v.redenCode === 'OVERIG' && (v.toelichting ?? '').length < 10) {
      ctx.addIssue({
        code: 'custom',
        path: ['toelichting'],
        message: 'Bij "Overig" is een toelichting van minstens 10 tekens verplicht',
      })
    }
  })
export type SaldoVerplaatsInput = z.infer<typeof SaldoVerplaatsSchema>

export interface SaldoVerplaatsEmmerStandDTO {
  emmerType: AfboekEmmerType
  jaar: number
  /** Bestaat de (TVT-)emmer nog niet, dan wordt hij aangemaakt op 0 (FO-522 §5.1). */
  bestaat: boolean
  voor: number
  na: number
  /**
   * Alleen bij een peildatum in het verleden en een andere stand vandaag (review
   * M3): de stand van vandaag en wat er dan overblijft. De bron-toets rekent met
   * de laagste van `na` en `naNu`.
   */
  nu?: number
  naNu?: number
  vervalDatum: string | null
}

export interface SaldoVerplaatsPreviewDTO {
  uren: number
  peildatum: string
  van: SaldoVerplaatsEmmerStandDTO
  naar: SaldoVerplaatsEmmerStandDTO
  /** De bron wordt negatief: alleen met `bevestigNegatief` (en een reden). */
  bronWordtNegatief: boolean
  /** De verplaatste uren nemen de vervaldatum van de doel-emmer (FO-513 §A3). */
  vervalDatumNaVerplaatsing: string | null
}

export interface SaldoVerplaatsResultDTO extends SaldoVerplaatsPreviewDTO {
  verplaatsingId: string
  vanMutatieId: string
  naarMutatieId: string
}

/** Foutcodes van de verplaats- en herverdeel-routes (inline melding in de dialog). */
export const SALDO_BLOKKADE_CODES = [
  'EINDAFREKENING_AFGEROND',
  'EIGEN_SALDO',
  'EMMER_ONBEKEND',
  'EMMER_VERVALLEN',
  'BRON_WORDT_NEGATIEF',
] as const
export type SaldoBlokkadeCode = (typeof SALDO_BLOKKADE_CODES)[number]

/** De ledger-stand per klassieke emmer (bron/doel-keuze in de verplaats-dialoog). */
export interface SaldoEmmerstandenDTO {
  employeeId: string
  naam: string
  emmers: Array<{
    emmerType: AfboekEmmerType
    jaar: number
    /** Ledger-stand (mag negatief zijn). */
    beschikbaar: number
    vervalDatum: string | null
  }>
  blokkade: { code: SaldoBlokkadeCode; message: string } | null
}

// ── Herverdeling incl. TVT (FO-522 §B) ───────────────────────────────────────

/** Uitbetaald of vervallen TVT telt niet mee in een herverdeling (§B7). */
export interface HerverdeelTvtBlokkadeDTO {
  emmerType: 'TVT_WERK' | 'TVT_REIS'
  uitbetaaldUren: number
  vervallenUren: number
  uitleg: string
}

export interface HerverdeelAanvraagDTO {
  verlofAanvraagId: string
  startDate: string
  endDate: string | null
  huidig: Array<{ emmerType: string; jaar: number; uren: number }>
  nieuw: Array<{ emmerType: string; jaar: number; uren: number }>
  /** Uren die de capaciteit niet dekt en dus op hun huidige emmer blijven staan. */
  tekort: number
}

export interface HerverdeelVorigJaarDTO {
  jaar: number
  aantalAanvragen: number
  uren: number
  uitleg: string
}

/** Bulk-herverdeling (per BV/jaar) - filter en uitvoering. */
export const HerverdeelBulkSchema = z.object({
  bvId: z.string().min(1).optional(),
  jaar: z.number().int().min(2020).max(2100).optional(),
  alleenNegatief: z.boolean().optional(),
  employeeIds: z.array(z.string().min(1)).max(500).optional(),
  dryRun: z.boolean().optional(),
})
export type HerverdeelBulkInput = z.infer<typeof HerverdeelBulkSchema>

export interface HerverdeelBulkRegelDTO {
  employeeId: string
  naam: string
  personeelsnummer: string | null
  changed: boolean
  aantalAanvragen: number
  /** Netto verschuiving van de afgeboekte uren per emmer-type (+ = meer afgeboekt). */
  nettoPerEmmer: Array<{ emmerType: string; uren: number }>
  /** Negatieve emmers na de herverdeling (som, positief getal). */
  resterendNegatief: number
  /** Negatieve emmers vóór de herverdeling (som, positief getal). */
  negatiefVooraf: number
  geblokkeerd: HerverdeelTvtBlokkadeDTO[]
  /** Aanvragen uit afgesloten jaren: niet herverdeeld, rapport voor een handmatig besluit (§B6). */
  vorigJaar: HerverdeelVorigJaarDTO[]
  blokkade: { code: SaldoBlokkadeCode; message: string } | null
  fout: string | null
}

export interface HerverdeelBulkResultDTO {
  dryRun: boolean
  regels: HerverdeelBulkRegelDTO[]
  totalen: {
    medewerkers: number
    gewijzigd: number
    aanvragen: number
    /** Uren die van ATV/wettelijk/bovenwettelijk naar TVT schuiven. */
    urenNaarTvt: number
    nettoPerEmmer: Array<{ emmerType: string; uren: number }>
    resterendNegatief: number
  }
}

// ── Saldi in de min (FO-522 §C9) ─────────────────────────────────────────────

export const SaldiInDeMinQuerySchema = z.object({
  bvId: z.string().min(1).optional(),
  employeeId: z.string().min(1).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
})
export type SaldiInDeMinQuery = z.infer<typeof SaldiInDeMinQuerySchema>

export interface SaldiInDeMinEmmerDTO {
  entitlementId: string
  emmerType: AfboekEmmerType
  jaar: number
  beschikbaar: number
}

export interface SaldiInDeMinRowDTO {
  employeeId: string
  naam: string
  personeelsnummer: string | null
  /** De emmers met een negatief saldo. */
  negatief: SaldiInDeMinEmmerDTO[]
  /** Som van de negatieve saldi (positief getal). */
  tekortTotaal: number
  tvtWerk: number
  tvtReis: number
  /** Kan (een deel van) het tekort met TVT worden rechtgetrokken? */
  kanRechttrekken: boolean
  /** Bv. eindafrekening bevestigd: dan zijn verplaatsen en herverdelen geblokkeerd. */
  blokkade: { code: SaldoBlokkadeCode; message: string } | null
}

export interface SaldiInDeMinResponse {
  data: SaldiInDeMinRowDTO[]
  meta: {
    total: number
    page: number
    pageSize: number
    tekortTotaal: number
    tvtBeschikbaarTotaal: number
  }
}

// ── Rechttrekken met TVT (FO-513 §A2) ────────────────────────────────────────

export const RechttrekBulkSchema = z
  .object({
    employeeIds: z.array(z.string().min(1)).max(500).optional(),
    bvId: z.string().min(1).optional(),
    dryRun: z.boolean().optional(),
    toelichting: z.string().trim().max(1000).optional(),
    peildatum: z.string().date().optional(),
  })
  .superRefine((v, ctx) => {
    // Zonder `dryRun` is het een dry-run (veilige default); alleen uitvoeren vraagt een reden.
    if (v.dryRun === false && (v.toelichting ?? '').length < 10) {
      ctx.addIssue({
        code: 'custom',
        path: ['toelichting'],
        message: 'Geef bij uitvoeren een reden van minstens 10 tekens',
      })
    }
  })
export type RechttrekBulkInput = z.infer<typeof RechttrekBulkSchema>

export interface RechttrekStapDTO {
  van: AfboekEmmerType
  vanJaar: number
  naar: AfboekEmmerType
  naarJaar: number
  uren: number
}

export interface RechttrekRegelDTO {
  employeeId: string
  naam: string
  personeelsnummer: string | null
  stappen: RechttrekStapDTO[]
  tvtIngezet: number
  tekortVooraf: number
  resterendTekort: number
  blokkade: { code: SaldoBlokkadeCode; message: string } | null
  fout: string | null
}

export interface RechttrekBulkResultDTO {
  dryRun: boolean
  regels: RechttrekRegelDTO[]
  totalen: {
    medewerkers: number
    metVoorstel: number
    tvtIngezet: number
    resterendTekort: number
  }
}

// ── Verwachte verdeling in de portal (FO-522 §A3) ────────────────────────────

/** Wat de medewerker bij zijn aanvraag ziet: "4 u gespaarde overuren, 4 u ATV". */
export interface VerwachteVerdelingDTO {
  delen: Array<{ emmerType: AfboekEmmerType; uren: number }>
  /** Niet door saldo gedekt: HR beslist (negatief, onbetaald of afwijzen). */
  tekort: number
}

/** De afboekvolgorde zoals de portal hem uitlegt (in leesbare volgorde). */
export interface AfboekVolgordeUitlegDTO {
  modus: AfboekVolgordeModusDTO
  emmers: AfboekEmmerType[]
}

/**
 * De emmers zoals ze midden in een zin heten (portal-uitleg, meldingen en
 * foutmeldingen): "ATV-uren" in plaats van "atv" (review L9).
 */
export const AFBOEK_EMMER_ZIN_LABELS: Record<AfboekEmmerType, string> = {
  TVT_WERK: 'gespaarde overuren (TVT)',
  TVT_REIS: 'gespaarde reistijd (TVT)',
  ATV: 'ATV-uren',
  WETTELIJK: 'wettelijk verlof',
  BOVENWETTELIJK: 'bovenwettelijk verlof',
}

/** "Bij verlof gaan eerst je gespaarde overuren (TVT) op, daarna ..." */
export function afboekVolgordeZin(uitleg: AfboekVolgordeUitlegDTO): string {
  if (uitleg.modus === 'VERVALT_EERST') {
    return 'Bij verlof gaat het saldo dat het eerst vervalt het eerst op.'
  }
  const namen = uitleg.emmers.map((e) => AFBOEK_EMMER_ZIN_LABELS[e])
  if (namen.length === 0) return ''
  if (namen.length === 1) return `Bij verlof gaan je ${namen[0]} op.`
  if (namen.length === 2) return `Bij verlof gaan eerst je ${namen[0]} op, daarna je ${namen[1]}.`
  return (
    `Bij verlof gaan eerst je ${namen[0]} op, daarna ${namen.slice(1, -1).join(', ')} ` +
    `en tot slot ${namen[namen.length - 1]}.`
  )
}

// ── Opbouwkeuzes TVT/ATV en de dubbel-indicator (FO-513 §B4, §C9) ────────────

/** Ten hoogste een jaar per overzicht of export (bestaande datumvalidatie, §5.5). */
export const OPBOUWKEUZES_MAX_DAGEN = 366

export const OpbouwkeuzesQuerySchema = z
  .object({
    van: z.string().date('Kies een geldige begindatum'),
    totEnMet: z.string().date('Kies een geldige einddatum'),
    bvId: z.string().min(1).optional(),
    afdelingId: z.string().min(1).optional(),
    employeeId: z.string().min(1).optional(),
    alleenAfwijkingen: queryBoolean().optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
  })
  .superRefine((v, ctx) => {
    if (v.totEnMet < v.van) {
      ctx.addIssue({ code: 'custom', path: ['totEnMet'], message: 'De einddatum ligt vóór de begindatum' })
      return
    }
    const dagen = (Date.parse(`${v.totEnMet}T00:00:00Z`) - Date.parse(`${v.van}T00:00:00Z`)) / 86_400_000
    if (dagen >= OPBOUWKEUZES_MAX_DAGEN) {
      ctx.addIssue({
        code: 'custom',
        path: ['totEnMet'],
        message: 'Kies een periode van hooguit een jaar (splits per jaar)',
      })
    }
  })
export type OpbouwkeuzesQuery = z.infer<typeof OpbouwkeuzesQuerySchema>

/**
 * Wie de keuze vastlegde: HR via de contractmutatie, de medewerker via een opbouwverzoek,
 * of de medewerker in de vakantiecampagne (batch-70, FO-525: de keuze voor het nieuwe jaar).
 */
export const OPBOUW_KEUZE_BRONNEN = ['HR', 'ZELFSERVICE', 'CAMPAGNE'] as const
export type OpbouwKeuzeBron = (typeof OPBOUW_KEUZE_BRONNEN)[number]
export const OPBOUW_KEUZE_BRON_LABELS: Record<OpbouwKeuzeBron, string> = {
  HR: 'HR',
  ZELFSERVICE: 'Zelfservice (opbouwverzoek)',
  CAMPAGNE: 'Vakantiecampagne',
}

/** Eén geldende keuze (een contractmutatie) binnen de periode. */
export interface OpbouwKeuzeSegmentDTO {
  mutatieId: string
  /** yyyy-mm-dd: ingangsdatum van de keuze. */
  vanaf: string
  /** yyyy-mm-dd of null (open einde). */
  tot: string | null
  atvEnabled: boolean
  tvtWerkurenEnabled: boolean
  tvtWerkOpbouwPercentage: number
  tvtReisurenEnabled: boolean
  tvtReisOpbouwPercentage: number
  reden: string
  bron: OpbouwKeuzeBron
  /** batch-70: bij bron CAMPAGNE de naam van de campagne (bijvoorbeeld "Vakantie 2027"). */
  bronCampagne?: string | null
  /** Naam van wie de mutatie vastlegde (bij zelfservice: de beoordelaar). */
  vastgelegdDoor: string | null
}

/** De verklaring bij een rode of oranje indicator (§B4). */
export const TVT_AFWIJKING_VERKLARINGEN = [
  'HANDMATIGE_REGEL',
  'CORRECTIEPAD',
  'KEUZEWISSEL',
  'OPBOUW_NIET_BIJGESTELD',
  'OPBOUW_ONTBREEKT',
  'ONBEKEND',
] as const
export type TvtAfwijkingVerklaring = (typeof TVT_AFWIJKING_VERKLARINGEN)[number]
export const TVT_AFWIJKING_VERKLARING_LABELS: Record<TvtAfwijkingVerklaring, string> = {
  HANDMATIGE_REGEL: 'Handmatige verloningsregel',
  CORRECTIEPAD: 'Uren-correctie na de verloning',
  KEUZEWISSEL: 'Keuze gewijzigd in de week of na de opbouw',
  OPBOUW_NIET_BIJGESTELD: 'Uren gedaald na de opbouw, TVT niet bijgesteld',
  OPBOUW_ONTBREEKT: 'TVT-opbouw ontbreekt of is te laag',
  ONBEKEND: 'Onbekende oorzaak',
}

/**
 * OK, DUBBEL (rood), TEKORT (oranje), of CONTROLEREN: een verloningsregel van vóór
 * batch-69 is handmatig gewijzigd of gecorrigeerd en de splitsing is niet
 * vastgelegd; de uitkomst is niet te herleiden (review H4, handmatig beoordelen).
 */
export type TvtUitkomstStatus = 'OK' | 'DUBBEL' | 'TEKORT' | 'CONTROLEREN'

/** De uitkomst van één week (overuren) of één dag (reistijd). */
export interface OpbouwUitkomstDTO {
  soort: 'WERK' | 'REIS'
  /** yyyy-Www (week) of yyyy-mm-dd (dag). */
  sleutel: string
  /** yyyy-mm-dd: maandag van de week of de dag zelf. */
  datum: string
  bruto: number
  /** De opbouwfactor volgens de keuze die toen gold (0, 0,5 of 1). */
  factor: number
  opgebouwd: number
  /** Uren die vol zijn uitbetaald (overuren 1,5x, reistijd 1x). */
  uitbetaald: number
  /** VERLONING = uit de vastgelegde verloningsregel; VERWACHT = nog niet verloond. */
  uitbetaaldBron: 'VERLONING' | 'VERWACHT'
  status: TvtUitkomstStatus
  dubbelUren: number
  tekortUren: number
  verklaring: TvtAfwijkingVerklaring | null
}

export interface OpbouwKeuzeTotalenDTO {
  overurenBruto: number
  overurenOpgebouwd: number
  overurenUitbetaald: number
  reisBruto: number
  reisOpgebouwd: number
  reisUitbetaald: number
  dubbelUren: number
  tekortUren: number
  /** Weken/dagen met status CONTROLEREN (handmatig beoordelen). */
  teControleren: number
}

export interface OpbouwKeuzeRegelDTO {
  employeeId: string
  naam: string
  personeelsnummer: string | null
  bvNaam: string | null
  keuzes: OpbouwKeuzeSegmentDTO[]
  /** Meer dan één keuze binnen de periode (wijziging midden in de periode). */
  keuzeGewijzigd: boolean
  totalen: OpbouwKeuzeTotalenDTO
  /** Alleen weken/dagen met uren of een afwijking. */
  uitkomsten: OpbouwUitkomstDTO[]
  /** Minstens één week/dag met status DUBBEL, TEKORT of CONTROLEREN. */
  afwijking: boolean
}

export interface OpbouwkeuzesResponse {
  data: OpbouwKeuzeRegelDTO[]
  meta: {
    total: number
    page: number
    pageSize: number
    van: string
    totEnMet: string
    /** Aantal medewerkers met een afwijking (over de hele gefilterde set). */
    metAfwijking: number
  }
}

/** yyyy-mm-dd én een echte kalenderdag (geen 2031-13-01). */
function isKalenderdag(ymd: string): boolean {
  const d = new Date(`${ymd}T00:00:00.000Z`)
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === ymd
}

/**
 * Integrator-review (delta-review, HOOG) - HR stelt de gespaarde uren van één week
 * bij naar de uren (Verlof > Opbouwkeuzes, "TVT bijstellen"): dezelfde zichtbare
 * bijstelling als de clawback (TVT_CLAWBACK op de week of dag, met audit), maar als
 * bewust besluit, ook als het beleid op "alleen signaleren" staat. De correctie hangt
 * zo aan de week: het overzicht wordt groen en een latere clawback stelt niet nog eens
 * bij. Een losse saldocorrectie (Verlof > Saldo) heeft die koppeling niet.
 */
export const TvtBijstellenSchema = z.object({
  employeeId: z.string().min(1, 'Kies een medewerker'),
  /** Welke regel: de overuren van de week (TVT_WERK) of de reistijd van een dag (TVT_REIS). */
  type: z.enum(['TVT_WERK', 'TVT_REIS']),
  /** Overuren: een dag in de week (de maandag); reistijd: de dag zelf. yyyy-mm-dd. */
  datum: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Gebruik een datum als jjjj-mm-dd')
    .refine(isKalenderdag, 'Dit is geen bestaande datum'),
  /** Toch bijstellen, ook als er na de opbouw al een losse TVT-correctie staat (409 LOSSE_CORRECTIE). */
  bevestigd: z.boolean().optional(),
})
export type TvtBijstellenInput = z.infer<typeof TvtBijstellenSchema>

/** Een losse handmatige TVT-correctie na de opbouw (details bij 409 LOSSE_CORRECTIE). */
export interface TvtLosseCorrectieDTO {
  /** yyyy-mm-dd: de peildatum van de correctie. */
  datum: string
  /** ISO-tijdstip waarop de correctie is geboekt. */
  aangemaaktOp: string
  uren: number
  toelichting: string | null
}

export interface TvtBijstellenResultDTO {
  /** yyyy-Www van de week van de regel. */
  week: string
  type: 'TVT_WERK' | 'TVT_REIS'
  /** yyyy-mm-dd: de weekmaandag (overuren) of de dag (reistijd). */
  datum: string
  /**
   * BIJGESTELD = de TVT van deze regel is verlaagd naar de uren; BIJGEBOEKT = de uren
   * bleken inmiddels hoger, de TVT is bijgeboekt; NIETS = de TVT klopt al met de uren.
   */
  uitkomst: 'BIJGESTELD' | 'BIJGEBOEKT' | 'NIETS'
  /** De verandering (negatief bij een bijstelling), 0 bij NIETS. */
  uren: number
  /** Na een bijstelling: de stand van de TVT-emmer (negatief = in de min). */
  standNa: number | null
}
